<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import type { CSSProperties } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import AnchoredPopup from '@/components/base/popover/AnchoredPopover.vue'
import PanelMenuLite from '@/components/base/panel-menu'
import { getWebpUrl } from '@/utils/webpAssets'
import type { WebpImageName } from '@/types/_webp'
import { VENUE_ICON_MAP } from '@/config/venueIcon'
import { VENUE_CODE } from '@/config/venue'
import SvgIcon from '@/components/SvgIcon.vue'
import type { SvgIconName } from '@/types'
import { MenuType, type NoneGameMenuItem, type GameMenuItem } from '@/types/gameMenu'
import { useI18n } from 'vue-i18n'
import { showRegister } from '@/utils/authDialog'
import { useGameCategory, type CategoryTabItem } from '@/composables/useGameCategory'
import Customer from '@/components/customer/index.vue'
import { decodeBase64UrlUtf8 } from '@/utils/base64'
import { copyToClip, openNewWindow } from '@/utils'
import { useShareDetail, type ShareItem } from '@/composables/useShareDetail'
import { useCustomerService } from '@/composables/useCustomerService'
import { useSupportLanguages } from '@/composables/useSupportLanguages'
import { showLanguageSwitchPopup } from '@/components/LanguageSwitchPopup'
import { getGameCategoryTarget } from '@/utils/gameCategoryRoute'
import useUserInfo from '@/composables/useUserInfo'

const { t } = useI18n()

const props = withDefaults(
  defineProps<{
    collapsed: boolean
    expandedWidth?: string
    collapsedWidth?: string
    animationDuration?: string
  }>(),
  {
    expandedWidth: '2.4rem',
    collapsedWidth: '0.7rem',
    animationDuration: '350ms'
  }
)

const route = useRoute()
const router = useRouter()
const { shareLink, fetchDetail, getShareIcons } = useShareDetail()
const { preview } = useUserInfo()

const { visibleLangList, currentLangCode } = useSupportLanguages()

const dynamicStyle = computed<CSSProperties>(() => ({
  '--side-menu-width': props.collapsed ? props.collapsedWidth : props.expandedWidth,
  '--side-menu-transition-duration': props.animationDuration
}))

const PopoverDataType = { ITEMS: 'items', TIP: 'tip' } as const
type PopupData =
  | { kind: typeof PopoverDataType.ITEMS; items: Array<NoneGameMenuItem | GameMenuItem> }
  | { kind: typeof PopoverDataType.TIP; tip: string }

const popupRef = ref<InstanceType<typeof AnchoredPopup> | null>(null)
const customerPopoverRef = ref<InstanceType<typeof AnchoredPopup> | null>(null)
// 普通客服面板统一锚定侧边栏 Live Chat，非侧边栏入口会复用这个 DOM 定位。
const liveChatTriggerRef = ref<HTMLElement | null>(null)
const popupData = ref<PopupData | undefined>(undefined)
const hoveredKey = ref<string>()
const { isCustomerPanelVisible, invokeCustomerPanel, dismissCustomerPanel, activeCSTab } =
  useCustomerService()

const handleRegister = () => {
  showRegister(() => {})
}

const handleItemClick = (item: GameMenuItem | NoneGameMenuItem, event: MouseEvent) => {
  if (item.key === 'liveChat') {
    invokeCustomerPanel()
    return
  }

  if (item.key === 'registerAction') {
    handleRegister()
    return
  }

  if (item.key === 'langSelector') {
    showLanguageSwitchPopup()
    return
  }

  if (typeof item.key === 'string' && item.key.startsWith('game-category-') && item.router) {
    if (route.path.startsWith('/game') || route.path === '/mini') {
      router.replace(item.router)
    } else {
      router.push(item.router)
    }
    return
  }

  if (route.name === 'Game' && item.type === MenuType.Game && item.router) {
    router.replace(item.router)
  } else if (item.router) {
    router.push(item.router)
  }
}

const handleCollapsedItemClick = (item: GameMenuItem | NoneGameMenuItem, event: MouseEvent) => {
  event.preventDefault()
  event.stopPropagation()
  if (item.items && item.items.length > 0 && popupRef.value) {
    popupData.value = { kind: PopoverDataType.ITEMS, items: item.items }
    popupRef.value.show(event)
    return
  }

  handleItemClick(item, event)
}

const handleItemHover = (item: GameMenuItem | NoneGameMenuItem, event: MouseEvent) => {
  hoveredKey.value = item.key

  if (props.collapsed && item.items?.length) {
    popupData.value = { kind: PopoverDataType.ITEMS, items: item.items }
    popupRef.value?.show(event)
  } else if ('tip' in item && item.tip) {
    popupData.value = { kind: PopoverDataType.TIP, tip: item.tip }
    popupRef.value?.show(event)
  }
}

const closePopup = () => {
  popupRef.value?.hideNow()
}

const setMenuItemRef = (item: GameMenuItem | NoneGameMenuItem, el: HTMLElement | null) => {
  if (item.key === 'liveChat') liveChatTriggerRef.value = el
}

const handleCustomerPopoverAfterHide = () => {
  if (!isCustomerPanelVisible.value) return
  dismissCustomerPanel()
}

watch(isCustomerPanelVisible, (visible) => {
  // SideMenu 是普通客服 popover 的唯一宿主，非侧边栏入口只改变全局显示状态。
  if (visible) {
    // 非点击入口没有 MouseEvent，直接复用 Live Chat 的 DOM 锚点定位。
    if (liveChatTriggerRef.value) customerPopoverRef.value?.show(liveChatTriggerRef.value)
  } else {
    customerPopoverRef.value?.hideNow()
  }
})

interface CategoryMenuItem extends CategoryTabItem {
  path?: string
  imgSrc?: string
}

const homeMenuItem = computed<CategoryMenuItem>(() => ({
  label: t('首页'),
  value: 0,
  code: 'HOME',
  id: 0,
  platformRespList: [],
  imgSrc: getWebpUrl('imgs/sideMenu/icon-home.webp'),
  path: '/'
}))

const { category, fetchGameCategories, gameCategoryData } = useGameCategory()

onMounted(() => {
  if (gameCategoryData.value.length === 0) {
    fetchGameCategories()
  }

  fetchDetail()
})

const menuCategory = computed<CategoryMenuItem[]>(() => [homeMenuItem.value, ...category.value])

const CATEGORY_ICON_MAP: Record<string, WebpImageName> = {
  HOME: 'imgs/sideMenu/icon-home.webp',
  ...VENUE_ICON_MAP
}

const getCategoryIconUrl = (code?: string): string => {
  const iconName = CATEGORY_ICON_MAP[String(code ?? '').toUpperCase()]
  return iconName ? getWebpUrl(iconName as WebpImageName) : ''
}

const isLoosePathActive = (currentRoute: typeof route, basePath?: string): boolean => {
  if (!basePath) return false

  let normalized = basePath.trim()
  if (!normalized.startsWith('/')) normalized = `/${normalized}`
  if (normalized.length > 1 && normalized.endsWith('/')) normalized = normalized.slice(0, -1)

  const currentPath = currentRoute.path
  if (normalized === '/') return currentPath === '/'
  if (currentPath === normalized) return true
  return currentPath.startsWith(`${normalized}/`)
}

const getRoutePlatformCode = (currentRoute: typeof route): string => {
  const queryCode = currentRoute.query.code
  const rawCode = Array.isArray(queryCode) ? queryCode[0] : queryCode
  if (!rawCode) return ''

  try {
    // 加密的场馆code
    return decodeBase64UrlUtf8(rawCode)
  } catch {
    // 兼容 code=ISLOT 这类明文参数。
    return rawCode
  }
}

// /game 分类不配置 item.path，需要结合 type 和平台 code 判断。
const isGameCategoryActive = (item: CategoryMenuItem): boolean => {
  if (!isLoosePathActive(route, '/game')) return false
  if (Number(route.query.type) !== Number(item.value)) return false

  const platformCode = getRoutePlatformCode(route).toUpperCase()
  // 特殊老虎机场馆，从电子大类中独立出来的
  if (item.code === VENUE_CODE.ISLOT) return platformCode === VENUE_CODE.ISLOT
  // 其他电子大类场馆
  if (item.code === VENUE_CODE.SLOT) return platformCode !== VENUE_CODE.ISLOT

  return true
}

const isCategoryActive = (item: CategoryMenuItem): boolean => {
  if (item.code === 'HOME') {
    return isLoosePathActive(route, '/')
  }

  if (item.path) {
    return isLoosePathActive(route, item.path)
  }

  return isGameCategoryActive(item)
}

const categoryMenus = computed<NoneGameMenuItem[]>(() => {
  return menuCategory.value.map((item): NoneGameMenuItem => {
    const target = item.path ?? getGameCategoryTarget(item)

    return {
      key: `game-category-${item.code ?? item.id}`,
      type: MenuType.Game,
      router: target,
      label: item.label ?? '',
      tip: item.label ?? '',
      iconUrl: item.imgSrc || getCategoryIconUrl(item.code),
      highlightIconUrl: undefined,
      active: isCategoryActive(item)
    }
  })
})

const shortcutMenus = computed<NoneGameMenuItem[]>(() => {
  const menus: NoneGameMenuItem[] = [
    {
      key: 'vip',
      router: '/vip',
      label: t('VIP俱乐部'),
      iconUrl: getWebpUrl('imgs/sideMenu/icon-vip-club.webp')
    },
    {
      key: 'promotion',
      router: '/promotions',
      label: t('促销活动'),
      iconUrl: getWebpUrl('imgs/sideMenu/icon-promotion.webp')
    },
    {
      key: 'referral',
      router: '/share',
      label: t('Referral'),
      iconUrl: getWebpUrl('imgs/sideMenu/icon-referral.webp')
    }
  ]
  return menus.map((item): NoneGameMenuItem => {
    return {
      ...item,
      tip: typeof item.label === 'string' ? item.label : '',
      active: isLoosePathActive(route, item.router)
    }
  })
})

const langConfig = computed(() => {
  const currentLang = visibleLangList.value.find((l) => l.value === currentLangCode.value)

  return {
    currentLang,
    isMultiLangEnabled: visibleLangList.value.length > 1
  }
})

const serviceMenus = computed<NoneGameMenuItem[]>(() => {
  const { currentLang, isMultiLangEnabled } = langConfig.value

  const menus: NoneGameMenuItem[] = [
    {
      key: 'infoCenter',
      router: '/about',
      label: t('Info Center'),
      iconUrl: getWebpUrl('imgs/sideMenu/icon-infocenter.webp')
    },
    {
      key: 'infoCenter',
      router: '/news',
      label: t('博客与新闻'),
      iconUrl: getWebpUrl('imgs/sideMenu/icon-news.webp')
    },
    {
      key: 'liveChat',
      label: t('Live Chat'),
      iconUrl: getWebpUrl('imgs/sideMenu/icon-livechat.webp')
    }
  ]

  if (isMultiLangEnabled && currentLang) {
    menus.push({
      key: 'langSelector',
      label: currentLang.name,
      tip: currentLang.name,
      iconUrl: getWebpUrl('imgs/sideMenu/icon-language.webp')
    })
  }

  return menus.map(
    (item): NoneGameMenuItem => ({
      ...item,
      tip: item.tip ?? (typeof item.label === 'string' ? item.label : ''),
      active: item.router ? isLoosePathActive(route, item.router) : false
    })
  )
})

const inviteShareItems = computed<ShareItem[]>(() => {
  return getShareIcons(shareLink.value, { hideCustom: true }).filter((item) =>
    [1, 3, 2, 5].includes(item.id)
  )
})

const handleInviteShareItemClick = (item: ShareItem) => {
  if (!item.link) return

  if (item.id === 1) {
    copyToClip({ content: shareLink.value, msg: t('复制成功') })
    return
  }

  if (item.id === 5) {
    location.href = item.link
    return
  }

  openNewWindow(item.link)
}

const menuGroups = computed<Array<NoneGameMenuItem[]>>(() => {
  return [categoryMenus.value, shortcutMenus.value, serviceMenus.value].filter(
    (group) => group.length > 0
  )
})

const showDiscountsDot = computed(() => {
  return preview.value.claimed > 0 || preview.value.rebatePreviewStatus
})
</script>

<template>
  <aside :class="['side-collapse-menu', { isCollapse: props.collapsed }]" :style="dynamicStyle">
    <template v-for="(group, groupIndex) in menuGroups" :key="`side-group-${groupIndex}`">
      <PanelMenuLite
        :model="group"
        :collapsed="props.collapsed"
        :item-ref="setMenuItemRef"
        @item-click="handleItemClick"
        @collapsed-item-click="handleCollapsedItemClick"
        @item-hover="handleItemHover"
        :pt="{ root: styles.pmRoot, panel: styles.panel, subItemWrp: styles.subWrap }">
        <template #header-icon="{ item, active }">
          <SvgIcon
            v-if="active && item.highlightIconUrl"
            :name="item.highlightIconUrl as SvgIconName"
            class="icon" />

          <span
            v-else-if="item.iconUrl"
            aria-hidden="true"
            class="icon"
            :style="{
              backgroundImage: `url(${item.iconUrl})`,
              backgroundRepeat: 'no-repeat'
            }" />
        </template>

        <template #header-extra="{ item, collapsed }">
          <span
            v-if="item.key === 'promotion' && showDiscountsDot"
            :class="['discounts-dot', { 'discounts-dot--collapsed': collapsed }]" />
        </template>
      </PanelMenuLite>

      <div v-if="groupIndex < menuGroups.length - 1" class="separator" />
    </template>

    <template v-if="!props.collapsed">
      <div v-if="menuGroups.length > 0" class="separator separator--bottom" />

      <div class="invite-section">
        <div class="invite-section__button">{{ t('邀请好友') }}</div>

        <div class="invite-section__icons">
          <button
            v-for="item in inviteShareItems"
            :key="item.id"
            class="invite-section__icon-button"
            type="button"
            @click="handleInviteShareItemClick(item)">
            <img :src="item.src" :alt="item.name" />
          </button>
        </div>
      </div>
    </template>
  </aside>

  <AnchoredPopup
    ref="customerPopoverRef"
    :rootClass="styles.customerPopover"
    placement="right"
    :gap="40"
    :viewportMargin="50"
    :showArrow="false"
    :zIndex="800"
    :closeOnHoverLeave="false"
    :clickOutsideIgnore="['.popup-wrapper']"
    @after-hide="handleCustomerPopoverAfterHide">
    <Customer @close="dismissCustomerPanel" :type="activeCSTab" />
  </AnchoredPopup>

  <!-- <AnchoredPopup
    ref="popupRef"
    :rootClass="styles.popoverWrap"
    @after-hide="hoveredKey = undefined"
    :gap="15"
    :viewportMargin="{ top: 0, bottom: 0 }">
    <ul v-if="popupData?.kind === 'items'" class="popover-menu-content">
      <template v-for="child in popupData.items" :key="child.key">
        <li
          class="popover-menu-item"
          @click="
            (e: MouseEvent) => {
              closePopup()
              handleItemClick(child, e)
            }
          ">
          <span
            v-if="child.iconUrl"
            aria-hidden="true"
            class="base-pm-icon"
            :style="{
              backgroundImage: `url(${child.iconUrl})`,
              backgroundRepeat: 'no-repeat'
            }" />
          <span class="popover-menu-label">{{ child.label }}</span>
        </li>
      </template>
    </ul>

    <div v-else-if="popupData?.kind === 'tip'" :class="styles.popTip">
      {{ popupData.tip }}
    </div>
  </AnchoredPopup> -->
</template>

<style scoped>
.isCollapse {
  .icon {
    margin-right: 0;
  }
}

.side-collapse-menu {
  background: #131313;
  width: var(--side-menu-width);
  position: sticky;
  top: 90px;
  flex-shrink: 0;
  transition: width var(--side-menu-transition-duration) cubic-bezier(0, 1, 0, 1);
  overflow-x: hidden;
  overflow-y: auto;
  white-space: nowrap;
  -ms-overflow-style: none;
  scrollbar-width: none;
  border-right: 0.4px solid #ffffff1a;
  padding-top: 6px;
}

.side-collapse-menu::-webkit-scrollbar {
  display: none;
}

.separator {
  background: #202020;
  height: 3px;
  margin: 15px 0;
}

.separator--bottom {
  margin-bottom: 12px;
}

.invite-section {
  border-radius: 10px;
  background: linear-gradient(90deg, #201e1c 0%, #2b2824 100%);
  box-shadow: 0 4px 4px 0 rgba(0, 0, 0, 0.25);
  padding: 14px 12px 16px;
  margin: 0 14px 16px;
}

.invite-section__button {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 24px;
  margin-bottom: 12px;
  color: #fff;
  font-size: 13px;
  line-height: 16px;
}

.invite-section__icons {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.invite-section__icon-button {
  width: 30px;
  height: 30px;
  border: 0;
  padding: 0;
  background: transparent;
  cursor: pointer;
  flex-shrink: 0;

  img {
    display: block;
    width: 100%;
    height: 100%;
  }
}

.icon {
  width: 24px;
  height: 24px;
  font-size: 14px;
  display: inline-flex;
  color: #fff;
  background-repeat: no-repeat;
  background-size: contain;
}

.discounts-dot {
  width: 8px;
  height: 8px;
  margin-left: 2px;
  background-color: #a6311e;
  border-radius: 50%;
  flex-shrink: 0;
  animation: attention-dot-blink 1.2s ease-in-out infinite;
}

.discounts-dot--collapsed {
  position: absolute;
  top: 8px;
  right: 8px;
  margin-left: 0;
  z-index: 1;
}

.register-icon {
  width: 20px;
  height: 20px;
  color: #fff;
}

.popover-menu-content {
  display: grid;
  grid-template-columns: 1fr;
  gap: 6px;
}

.popover-menu-item {
  display: flex;
  align-items: center;
  padding: 8px 10px;
  color: #eee;
  text-decoration: none;
  cursor: pointer;
  white-space: nowrap;
  border-radius: 6px;
}

.popover-menu-item:hover {
  background: rgba(255, 255, 255, 0.08);
}

.popover-menu-label {
  margin-left: 8px;
  flex-grow: 1;
}

.base-pm-icon {
  width: 18px;
  height: 18px;
  background-size: contain;
  background-repeat: no-repeat;
  flex-shrink: 0;
}
</style>

<style module="styles">
.pmRoot {
  --pm-expand-panel-bg: rgba(255, 255, 255, 0.02);
}

.subWrap {
  margin: 10px 0;
}

.popoverWrap {
  box-shadow: 4px 0px 4px 0px #00000005;
  --anchor-po-padding: 8px;
  --anchor-po-bg: url('@/assets/imgs/common/common_noise_bg.webp') 0 0 / 66px 66px repeat, #3a1d66;
}

.customerPopover {
  --anchor-po-padding: 0;
  --anchor-po-bg: transparent;
  --anchor-po-whole-border: none;
  --anchor-po-box-shadow: 0px 4px 20px 0px #00000099;
  --anchor-po-box-radius: 16px;
  --anchor-po-overflow-y: hidden;

  :global(.anchor-po) {
    height: var(--anchor-po-max-height);
  }

  :global(.anchor-po-content) {
    box-sizing: border-box;
    height: 100%;
  }
}

.popTip {
  color: #fff;
  font-size: 14px;
  line-height: 24px;
  font-weight: 500;
  padding: 2px;
}
</style>
